import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';

const COLORS = [
  '#3B82F6', // blue
  '#10B981', // green
  '#F59E0B', // amber
  '#8B5CF6', // purple
  '#EC4899', // pink
  '#EF4444', // red
];

const MAX_POINTS = 60;

const BreathRatePanel: React.FC = () => {
  const breathRateByDevice = useSelector((state: RootState) => state.dashboard.breathRateByDevice);

  const devices = Object.keys(breathRateByDevice).sort();

  // Calculate stats across all devices
  const stats = useMemo(() => {
    const allData: { rate: number }[] = [];
    Object.values(breathRateByDevice).forEach(data => {
      allData.push(...data);
    });
    
    if (allData.length === 0) {
      return { avg: 0, min: 0, max: 0 };
    }
    
    const avg = Math.round((allData.reduce((sum, d) => sum + d.rate, 0) / allData.length) * 10) / 10;
    const min = Math.min(...allData.map(d => d.rate));
    const max = Math.max(...allData.map(d => d.rate));
    
    return { avg, min, max };
  }, [breathRateByDevice]);

  // Get latest values per device
  const latestByDevice = useMemo(() => {
    const result: { device: string; rate: number }[] = [];
    devices.forEach(device => {
      const data = breathRateByDevice[device];
      if (data && data.length > 0) {
        result.push({ device, rate: data[data.length - 1].rate });
      }
    });
    return result;
  }, [breathRateByDevice, devices]);

  // Prepare chart data
  const chartData = useMemo(() => {
    if (devices.length === 0) return { points: [], timeLabels: [] };
    
    const points: { idx: number; values: Record<string, number | null> }[] = [];
    
    // Get all unique timestamps across all devices, sorted
    const allTimestamps = new Set<number>();
    Object.values(breathRateByDevice).forEach(data => {
      data.forEach(point => allTimestamps.add(point.timestamp));
    });
    
    const sortedTimestamps = Array.from(allTimestamps).sort((a, b) => a - b);
    const displayTimestamps = sortedTimestamps.slice(-MAX_POINTS);
    
    displayTimestamps.forEach((ts, idx) => {
      const values: Record<string, number | null> = {};
      devices.forEach((device, i) => {
        const deviceData = breathRateByDevice[device];
        const dataPoint = deviceData.find(d => d.timestamp === ts);
        values[`device${i}`] = dataPoint?.rate ?? null;
      });
      points.push({ idx, values });
    });

    // Generate time labels
    const timeLabels: { x: number; label: string }[] = [];
    if (displayTimestamps.length > 0) {
      displayTimestamps.forEach((ts, idx) => {
        const time = new Date(ts);
        const label = `${time.getMinutes().toString().padStart(2, '0')}:${time.getSeconds().toString().padStart(2, '0')}`;
        if (idx === 0 || idx === displayTimestamps.length - 1) {
          timeLabels.push({ x: idx, label });
        } else if (displayTimestamps.length > 30 && idx % Math.floor(displayTimestamps.length / 4) === 0) {
          timeLabels.push({ x: idx, label });
        }
      });
    }
    
    return { points, timeLabels };
  }, [breathRateByDevice, devices]);

  if (devices.length === 0) {
    return (
      <div className="bg-white dark:bg-claude-darkSurface rounded-xl p-4 shadow-sm border border-gray-200 dark:border-claude-darkBorder">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full flex items-center justify-center bg-blue-500 bg-opacity-20">
            <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
            </svg>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-claude-darkText">Breath Rate</h3>
            <p className="text-sm text-gray-500 dark:text-claude-darkTextSecondary">Waiting for data...</p>
          </div>
        </div>
        <div className="h-48 flex items-center justify-center text-gray-400 dark:text-claude-darkTextSecondary">
          No breath rate data received
        </div>
      </div>
    );
  }

  // SVG chart dimensions
  const width = 500;
  const height = 180;
  const padding = { top: 10, right: 10, bottom: 30, left: 40 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  const xScale = (idx: number) => padding.left + (chartData.points.length > 1 ? (idx / (chartData.points.length - 1)) * chartWidth : 0);
  const yScale = (val: number) => padding.top + chartHeight - ((val - 10) / 20) * chartHeight;

  // Build path for each device, handling gaps
  const buildPath = (deviceIndex: number): string | null => {
    const key = `device${deviceIndex}`;
    const points = chartData.points.filter(p => p.values[key] !== null);
    if (points.length === 0) return null;
    
    return points.map((p, i) => {
      const x = xScale(p.idx);
      const y = yScale(p.values[key]!);
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
    }).join(' ');
  };

  return (
    <div className="bg-white dark:bg-claude-darkSurface rounded-xl p-4 shadow-sm border border-gray-200 dark:border-claude-darkBorder">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center bg-blue-500 bg-opacity-20">
            <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
            </svg>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-claude-darkText">Breath Rate</h3>
            <p className="text-sm text-gray-500 dark:text-claude-darkTextSecondary">
              {devices.length === 1 ? 'Real-time monitoring' : `${devices.length} devices`}
            </p>
          </div>
        </div>
        {/* Current values per device */}
        <div className="text-right">
          {latestByDevice.map(({ device, rate }, i) => (
            <div key={device} className="flex items-center justify-end gap-2">
              {devices.length > 1 && (
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
              )}
              <span className="text-2xl font-bold text-gray-900 dark:text-claude-darkText">
                {rate.toFixed(1)}
              </span>
              <span className="text-xs text-gray-500">/min</span>
            </div>
          ))}
        </div>
      </div>

      {/* Device Legend */}
      {devices.length > 1 && (
        <div className="flex flex-wrap gap-3 mb-2">
          {devices.map((device, i) => (
            <div key={device} className="flex items-center gap-1.5">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: COLORS[i % COLORS.length] }}
              />
              <span className="text-xs text-gray-600 dark:text-claude-darkTextSecondary">
                {device}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Chart */}
      <div className="relative" style={{ height: `${height}px` }}>
        <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet">
          {/* Y-axis labels */}
          <text x={padding.left - 8} y={padding.top + 5} textAnchor="end" className="text-[10px] fill-gray-400">30</text>
          <text x={padding.left - 8} y={padding.top + chartHeight / 2 + 4} textAnchor="end" className="text-[10px] fill-gray-400">20</text>
          <text x={padding.left - 8} y={height - padding.bottom + 5} textAnchor="end" className="text-[10px] fill-gray-400">10</text>
          
          {/* Grid lines */}
          <line x1={padding.left} y1={padding.top} x2={width - padding.right} y2={padding.top} stroke="#e5e7eb" strokeOpacity={0.5} />
          <line x1={padding.left} y1={padding.top + chartHeight / 2} x2={width - padding.right} y2={padding.top + chartHeight / 2} stroke="#e5e7eb" strokeOpacity={0.5} />
          <line x1={padding.left} y1={height - padding.bottom} x2={width - padding.right} y2={height - padding.bottom} stroke="#e5e7eb" strokeOpacity={0.5} />
          
          {/* Reference line at 16 /min */}
          <line x1={padding.left} y1={yScale(16)} x2={width - padding.right} y2={yScale(16)} stroke="#3B82F6" strokeOpacity={0.3} strokeDasharray="4,4" />
          
          {/* Lines for each device */}
          {devices.map((device, i) => {
            const pathData = buildPath(i);
            if (!pathData) return null;
            
            return (
              <path
                key={device}
                d={pathData}
                fill="none"
                stroke={COLORS[i % COLORS.length]}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            );
          })}
          
          {/* X-axis time labels */}
          {chartData.timeLabels.map(({ x, label }) => (
            <text key={`t-${x}`} x={xScale(x)} y={height - 8} textAnchor="middle" className="text-[9px] fill-gray-400">
              {label}
            </text>
          ))}
        </svg>
      </div>

      {/* Stats */}
      <div className="flex justify-between mt-2 text-xs text-gray-500 dark:text-claude-darkTextSecondary">
        <span>Min: {stats.min > 0 ? stats.min.toFixed(1) : '--'}</span>
        <span>Avg: {stats.avg > 0 ? stats.avg.toFixed(1) : '--'}</span>
        <span>Max: {stats.max > 0 ? stats.max.toFixed(1) : '--'}</span>
      </div>
    </div>
  );
};

export default BreathRatePanel;